import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './masive-measurements-qc.scss';
import DataGrid, { Column, Editing, KeyboardNavigation, Scrolling, Sorting, StateStoring, Selection, FilterRow, HeaderFilter } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import { useApiMeasurementsQC } from '../../api/api.measurementsQC';
import { useApiConfigQC } from '../../api/api.configQC';
import { ConfiguracionQcDTO, CreateMedicionQcDTO, MedicionQcDTO } from '../../types/types.qualityControl';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useAppSelector } from '../../contexts/hooks';
import { newToast } from '../../components';
import TagBox from 'devextreme-react/tag-box';
import { Chart, Series, ArgumentAxis, ValueAxis, Legend, Tooltip as ChartTooltip, CommonSeriesSettings, Label, Point } from 'devextreme-react/chart';
import ScrollView from 'devextreme-react/scroll-view';
import { Toolbar } from 'devextreme-react/toolbar';
import { Item as ToolbarItem } from 'devextreme-react/toolbar';
import { resButtonRefresh } from '../../images/icons';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import Popup from 'devextreme-react/popup';
import TextArea from 'devextreme-react/text-area';
import CheckBox from 'devextreme-react/check-box';
import { resGridNotes, resGridNotesCheck } from '../../images/icons';
import { ConvertLocalDate } from '../../utils/functions';

const MasiveMeasurementsQC = () => {
  // Permisos
  const Prm_QC_R = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_R) ?? false;
  const Prm_QC_C = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_C) ?? false;
  const userId = useAppSelector((x) => x.userInfo.User?.Us_Id) ?? 0;
  const userLocation = Number(useAppSelector((x) => x.userInfo.Location) ?? 0);

  // API Hooks
  const { registerMeasurement, updateMeasurement, getLeveyJenningsData, getMeasurementsByConfiguration } = useApiMeasurementsQC();
  const { getConfigurationsBySede } = useApiConfigQC();

  // Referencias
  const gridRef = useRef<DataGrid>(null);

  // Estados
  const [materialesLotes, setMaterialesLotes] = useState<Array<{ MaterialLote: string; MaterialNombre: string; LoteCodigo: string }>>([]);
  const [selectedMaterialLotes, setSelectedMaterialLotes] = useState<string[]>([]);
  const [analitos, setAnalitos] = useState<any[]>([]);
  const [analitoDataSource, setAnalitoDataSource] = useState<DataSource | null>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [selectedAnalitoId, setSelectedAnalitoId] = useState<number | null>(null);
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const [selectedAnalitoNombre, setSelectedAnalitoNombre] = useState<string>('');
  const [chartsData, setChartsData] = useState<Array<{ controlKey: string; controlName: string; data: any }>>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [medicionesAnalito, setMedicionesAnalito] = useState<MedicionQcDTO[]>([]);
  const medicionesGridRef = useRef<DataGrid>(null);
  const [configuracionesMap, setConfiguracionesMap] = useState<Map<string, ConfiguracionQcDTO>>(new Map());

  // Cachés para optimización de rendimiento
  const [configuracionesCache, setConfiguracionesCache] = useState<ConfiguracionQcDTO[] | null>(null);
  const [medicionesCache, setMedicionesCache] = useState<Map<number, MedicionQcDTO[]>>(new Map());
  const [graficosCache, setGraficosCache] = useState<Map<number, any>>(new Map());

  // Estados para el modal de notas
  const [notaModalVisible, setNotaModalVisible] = useState(false);
  const [notaActual, setNotaActual] = useState('');
  const [notaAnalitoId, setNotaAnalitoId] = useState<number | null>(null);
  const [notaMaterialLoteKey, setNotaMaterialLoteKey] = useState<string>('');

  // Estados para controles de visualización de gráficos
  const [nivelesJuntos, setNivelesJuntos] = useState(false);
  const [mostrarLineas, setMostrarLineas] = useState(true);

  useEffect(() => {
    if (Prm_QC_R && !configuracionesCache) {
      loadMaterialesLotes();
    }
  }, [Prm_QC_R, userLocation]);

  const loadMaterialesLotes = async () => {
    try {
      // Usar caché si existe y la sede no ha cambiado
      let configs = configuracionesCache;
      if (!configs) {
        configs = await getConfigurationsBySede(userLocation, true);
        setConfiguracionesCache(configs);
      }
      if (configs && configs.length > 0) {
        // Agrupar por Material + Lote
        const grouped = configs.reduce((acc: any, config) => {
          const key = `${config.MaterialNombre}|${config.LoteCodigo}`;
          if (!acc[key]) {
            acc[key] = {
              MaterialLote: key,
              MaterialNombre: config.MaterialNombre,
              LoteCodigo: config.LoteCodigo,
            };
          }
          return acc;
        }, {});
        setMaterialesLotes(Object.values(grouped));
      }
    } catch (error) {
      console.error('Error cargando materiales/lotes:', error);
      newToast('ERROR', 'Error al cargar controles de calidad', 5);
    }
  };

  const handleMaterialLoteChange = async (materialLotes: string[]) => {
    setSelectedMaterialLotes(materialLotes);
    setSelectedAnalitoId(null);
    setSelectedConfigId(null);
    setSelectedAnalitoNombre('');
    setChartData(null);
    setMedicionesAnalito([]);
    setSelectedTab(0);

    if (!materialLotes || materialLotes.length === 0) {
      setAnalitos([]);
      setAnalitoDataSource(null);
      return;
    }

    try {
      // Usar caché de configuraciones si existe
      let configs = configuracionesCache;
      if (!configs) {
        configs = await getConfigurationsBySede(userLocation, true);
        setConfiguracionesCache(configs);
      }

      // Crear mapa de configuraciones para acceso rápido
      const configMap = new Map<string, ConfiguracionQcDTO>();

      // Filtrar configuraciones seleccionadas
      const selectedConfigs = configs.filter((c) => {
        const key = `${c.MaterialNombre}|${c.LoteCodigo}`;
        return materialLotes.includes(key);
      });

      // Mapear configuraciones por Material-Lote
      selectedConfigs.forEach((config) => {
        const key = `${config.MaterialNombre}|${config.LoteCodigo}`;
        configMap.set(`${key}|${config.Qcc_An_Id}`, config);
      });

      setConfiguracionesMap(configMap);

      // Agrupar analitos comunes entre los controles seleccionados
      const analitosByAnId = new Map<number, any>();

      // Cargar mediciones del día actual para cada configuración
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const medicionesDelDia = new Map<number, any>();

      // Obtener mediciones del día para cada configuración (usando caché)
      for (const config of selectedConfigs) {
        try {
          // Verificar caché primero
          let mediciones = medicionesCache.get(config.Qcc_Id);
          if (!mediciones) {
            mediciones = await getMeasurementsByConfiguration(config.Qcc_Id);
            if (mediciones && mediciones.length > 0) {
              setMedicionesCache((prev) => new Map(prev).set(config.Qcc_Id, mediciones!));
            }
          }
          const medicionHoy = mediciones?.find((m: any) => {
            const fechaMedicion = new Date(ConvertLocalDate(m.Qcme_FechaHora));
            return fechaMedicion >= today && fechaMedicion < tomorrow;
          });

          if (medicionHoy) {
            medicionesDelDia.set(config.Qcc_Id, medicionHoy);
          }
        } catch (error) {
          console.error('Error cargando mediciones para config:', config.Qcc_Id, error);
        }
      }

      selectedConfigs.forEach((config) => {
        const materialLoteKey = `${config.MaterialNombre}|${config.LoteCodigo}`;

        if (!analitosByAnId.has(config.Qcc_An_Id)) {
          analitosByAnId.set(config.Qcc_An_Id, {
            Qcc_An_Id: config.Qcc_An_Id,
            AnalitoNombre: config.AnalitoNombre || '',
            controles: {},
          });
        }

        const analito = analitosByAnId.get(config.Qcc_An_Id);
        const medicionExistente = medicionesDelDia.get(config.Qcc_Id);

        analito.controles[materialLoteKey] = {
          Qcc_Id: config.Qcc_Id,
          MaterialNombre: config.MaterialNombre,
          MaterialNivel: config.MaterialNivel,
          LoteCodigo: config.LoteCodigo,
          Qcc_MediaEsperada: config.Qcc_MediaEsperada,
          Qcc_DesviacionEst: config.Qcc_DesviacionEst,
          Qcc_N: config.Qcc_N,
          Valor: medicionExistente?.Qcme_Valor ?? null,
          Estado: medicionExistente ? 'saved' : 'pending',
          Qcme_Id: medicionExistente?.Qcme_Id,
          Qcme_FechaHora: medicionExistente?.Qcme_FechaHora,
          ZScore: medicionExistente?.ZScore,
          Qcme_Estado: medicionExistente?.Qcme_Estado,
          TieneViolaciones: medicionExistente?.TieneViolaciones,
          Observacion: medicionExistente?.Qcme_Observaciones,
          Nota: medicionExistente?.Qcme_ComentarioValidacion ?? '',
        };
      });

      const analitosList = Array.from(analitosByAnId.values());
      setAnalitos(analitosList);

      // Crear CustomStore para manejar las actualizaciones
      const customStore = new CustomStore({
        key: 'Qcc_An_Id',
        load: () => {
          // Siempre devolver el estado actual de analitos
          return new Promise((resolve) => {
            setAnalitos((current) => {
              resolve(current);
              return current;
            });
          });
        },
        update: async (key: number, values: any) => {
          const analito = analitosList.find((a) => a.Qcc_An_Id === key);
          if (!analito) {
            console.error('No se encontró el analito con Qcc_An_Id:', key);
            return Promise.reject('Analito no encontrado');
          }

          // Identificar qué columna de control se está actualizando
          const updatePromises: Promise<any>[] = [];
          const savingFlags = new Map<string, boolean>();

          for (const [controlKey, newValue] of Object.entries(values)) {
            if (!controlKey.startsWith('Valor_') || newValue === null || newValue === undefined) {
              continue;
            }

            const materialLoteKey = controlKey.replace('Valor_', '');
            const control = analito.controles[materialLoteKey];

            if (!control) {
              console.error('No se encontró control:', materialLoteKey);
              continue;
            }

            // Prevenir doble guardado
            const saveKey = `${key}_${materialLoteKey}`;
            if (savingFlags.get(saveKey)) {
              continue;
            }
            savingFlags.set(saveKey, true);

            // Actualizar estado a "saving"
            setAnalitos((prev) =>
              prev.map((a) => {
                if (a.Qcc_An_Id === key) {
                  return {
                    ...a,
                    controles: {
                      ...a.controles,
                      [materialLoteKey]: {
                        ...a.controles[materialLoteKey],
                        Valor: newValue,
                        Estado: 'saving',
                      },
                    },
                  };
                }
                return a;
              })
            );

            const savePromise = (async () => {
              try {
                let result;
                let esActualizacion = false;

                // Si control.Qcme_Id existe, significa que ya cargamos una medición del día actual
                // Por lo tanto, debemos actualizarla en lugar de crear una nueva
                if (control.Qcme_Id) {
                  // Actualizar medición existente
                  esActualizacion = true;
                  const updateDto = {
                    Qcme_Id: control.Qcme_Id,
                    Qcme_Qcc_Id: control.Qcc_Id,
                    Qcme_Valor: newValue as number,
                    Qcme_Us_Id: userId,
                    Qcme_ComentarioValidacion: control.Nota || undefined,
                  };

                  result = await updateMeasurement(updateDto);
                } else {
                  // Crear nueva medición
                  const createDto: CreateMedicionQcDTO = {
                    Qcme_Qcc_Id: control.Qcc_Id,
                    Qcme_Valor: newValue as number,
                    Qcme_ComentarioValidacion: control.Nota || undefined,
                    Qcme_Us_Id: userId,
                  };

                  result = await registerMeasurement(createDto);
                }

                // Actualizar la fila con los datos del resultado
                // IMPORTANTE: Si fue actualización, mantener el ID original. Si fue creación, usar el nuevo ID
                const medicionId = esActualizacion ? control.Qcme_Id : result?.Qcme_Id;
                const fechaHora = result?.Qcme_FechaHora || control.Qcme_FechaHora;

                setAnalitos((prev) =>
                  prev.map((a) => {
                    if (a.Qcc_An_Id === key) {
                      return {
                        ...a,
                        controles: {
                          ...a.controles,
                          [materialLoteKey]: {
                            ...a.controles[materialLoteKey],
                            Valor: newValue,
                            Estado: 'saved',
                            Qcme_Id: medicionId,
                            Qcme_FechaHora: fechaHora,
                            ZScore: result?.ZScore,
                            Qcme_Estado: result?.Qcme_Estado || 2,
                            TieneViolaciones: result?.TieneViolaciones || false,
                          },
                        },
                      };
                    }
                    return a;
                  })
                );

                const accion = esActualizacion ? 'actualizada' : 'registrada';
                newToast('SUCCESS', `Medición ${accion} para ${analito.AnalitoNombre} - ${control.MaterialNombre}`, 2);
              } catch (error) {
                console.error('Error guardando medición:', error);
                savingFlags.delete(`${key}_${materialLoteKey}`);
                setAnalitos((prev) =>
                  prev.map((a) => {
                    if (a.Qcc_An_Id === key) {
                      return {
                        ...a,
                        controles: {
                          ...a.controles,
                          [materialLoteKey]: {
                            ...a.controles[materialLoteKey],
                            Estado: 'error',
                            Valor: newValue, // Mantener el valor ingresado incluso si hay error
                          },
                        },
                      };
                    }
                    return a;
                  })
                );
                newToast('ERROR', `Error al guardar medición para ${analito.AnalitoNombre}`, 5);
                throw error;
              }
            })();

            updatePromises.push(savePromise);
          }

          if (updatePromises.length > 0) {
            await Promise.all(updatePromises);

            // Actualizar caché de mediciones para la configuración modificada
            const analito = analitosList.find((a) => a.Qcc_An_Id === key);
            if (analito) {
              for (const [controlKey, newValue] of Object.entries(values)) {
                if (controlKey.startsWith('Valor_') && newValue !== null && newValue !== undefined) {
                  const materialLoteKey = controlKey.replace('Valor_', '');
                  const control = analito.controles[materialLoteKey];
                  if (control?.Qcc_Id) {
                    // Invalidar caché de mediciones para esta configuración
                    setMedicionesCache((prev) => {
                      const newCache = new Map(prev);
                      newCache.delete(control.Qcc_Id);
                      return newCache;
                    });
                  }
                }
              }
            }
            // No es necesario hacer refresh, el estado ya se actualizó
          }

          return Promise.resolve();
        },
      });

      const dataSource = new DataSource({
        store: customStore,
        reshapeOnPush: true,
      });

      setAnalitoDataSource(dataSource);
    } catch (error) {
      console.error('Error cargando analitos:', error);
      newToast('ERROR', 'Error al cargar analitos', 5);
    }
  };

  const handleVerDetalle = async (rowData: any) => {
    const controlKeys = Object.keys(rowData.controles);
    if (controlKeys.length === 0) return;

    const firstControl = rowData.controles[controlKeys[0]];

    setSelectedAnalitoId(rowData.Qcc_An_Id);
    setSelectedConfigId(firstControl.Qcc_Id);
    setSelectedAnalitoNombre(rowData.AnalitoNombre);

    // Cargar mediciones de TODOS los controles (usando caché)
    const medicionesPromises = controlKeys.map(async (controlKey) => {
      const control = rowData.controles[controlKey];
      const [materialNombre] = controlKey.split('|');

      // Extraer nombre del nivel
      let controlName = materialNombre;
      const nivelMatch = materialNombre.match(/(Nivel|Level)\s*([1-3])/i);
      if (nivelMatch) {
        controlName = `${nivelMatch[1]} ${nivelMatch[2]}`;
      }

      try {
        // Verificar caché primero
        let mediciones = medicionesCache.get(control.Qcc_Id);
        if (!mediciones) {
          mediciones = await getMeasurementsByConfiguration(control.Qcc_Id);
          if (mediciones && mediciones.length > 0) {
            setMedicionesCache((prev) => new Map(prev).set(control.Qcc_Id, mediciones!));
          }
        }
        // Agregar el nombre del nivel a cada medición
        return (mediciones || []).map((m: any) => ({
          ...m,
          NivelNombre: controlName,
          MaterialNombre: materialNombre,
        }));
      } catch (error) {
        console.error(`Error cargando mediciones para ${controlName}:`, error);
        return [];
      }
    });

    const medicionesArrays = await Promise.all(medicionesPromises);
    const todasLasMediciones = medicionesArrays.flat();
    // Ordenar por fecha descendente
    todasLasMediciones.sort((a, b) => new Date(b.Qcme_FechaHora).getTime() - new Date(a.Qcme_FechaHora).getTime());
    setMedicionesAnalito(todasLasMediciones);

    // Cargar gráficos de TODOS los controles del analito (usando caché)
    const chartsPromises = controlKeys.map(async (controlKey) => {
      const control = rowData.controles[controlKey];
      const [materialNombre] = controlKey.split('|');

      // Obtener el nivel desde el control
      const materialNivel = control.MaterialNivel || '';

      // Crear nombre completo: Material + Nivel
      const controlName = materialNivel ? `${materialNombre} - ${materialNivel}` : materialNombre;

      // Verificar caché primero
      let data = graficosCache.get(control.Qcc_Id);
      if (!data) {
        data = await getLeveyJenningsData(control.Qcc_Id);
        if (data) {
          setGraficosCache((prev) => new Map(prev).set(control.Qcc_Id, data));
        }
      }
      return {
        controlKey,
        controlName,
        data: data || null,
      };
    });

    const chartsResults = await Promise.all(chartsPromises);
    setChartsData(chartsResults);

    // Cambiar a la pestaña de detalle
    setSelectedTab(1);
  };

  const handleRefresh = useCallback(() => {
    if (selectedMaterialLotes && selectedMaterialLotes.length > 0) {
      // Limpiar cachés de mediciones para forzar recarga
      setMedicionesCache(new Map());
      handleMaterialLoteChange(selectedMaterialLotes);
    }
  }, [selectedMaterialLotes]);

  const renderEstadoCeldaMedicion = useCallback((cellData: any) => {
    const estado = cellData.value;
    if (!estado) return <div style={{ textAlign: 'center' }}>-</div>;

    let texto = '';
    let color = '';

    switch (estado) {
      case 1: // PENDIENTE
        texto = 'PENDIENTE';
        color = '#FFA500';
        break;
      case 2: // VALIDADO
        texto = 'VALIDADO';
        color = '#4CAF50';
        break;
      case 3: // RECHAZADO
        texto = 'RECHAZADO';
        color = '#F44336';
        break;
      case 4: // ALERTA
        texto = 'ALERTA';
        color = '#FF5722';
        break;
      default:
        texto = 'DESCONOCIDO';
        color = '#9E9E9E';
    }

    return (
      <div
        style={{
          backgroundColor: color,
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '11px',
        }}>
        {texto}
      </div>
    );
  }, []);

  const renderViolacionCelda = useCallback((cellData: any) => {
    if (!cellData.data.TieneViolaciones) {
      return <div style={{ textAlign: 'center' }}>-</div>;
    }

    return (
      <div
        style={{
          backgroundColor: '#FF5722',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '11px',
        }}>
        ⚠ SÍ
      </div>
    );
  }, []);

  const renderComentarioCelda = useCallback((cellData: any) => {
    const notaValidacion = cellData.data.Qcme_ComentarioValidacion;
    const observaciones = cellData.data.Qcme_Observaciones;
    const violaciones = cellData.data.Violaciones;

    if (cellData.data.TieneViolaciones && violaciones && violaciones.length > 0) {
      // Mostrar las reglas violadas
      const reglasVioladas = violaciones.map((v: any) => v.Qcv_Regla).join(', ');
      const descripcionCompleta = violaciones.map((v: any) => v.Qcv_Descripcion).join(' | ');

      return (
        <div style={{ padding: '4px' }}>
          <div style={{ fontWeight: 'bold', color: '#F44336', marginBottom: '4px' }}>Reglas violadas: {reglasVioladas}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>{descripcionCompleta}</div>
          {observaciones && <div style={{ marginTop: '4px', fontStyle: 'italic', borderTop: '1px solid #ddd', paddingTop: '4px' }}>{observaciones}</div>}
        </div>
      );
    }

    return <div style={{ padding: '4px' }}>{observaciones || '-'}</div>;
  }, []);

  const renderVerDetalleButton = useCallback((cellData: any) => {
    const rowData = cellData.data;
    return (
      <div style={{ textAlign: 'center' }}>
        <Button text='Ver Mediciones' onClick={() => handleVerDetalle(rowData)} stylingMode='contained' type='default' />
      </div>
    );
  }, []);

  // Abrir modal de nota
  const handleOpenNotaModal = useCallback((analitoId: number, materialLoteKey: string, notaActual: string) => {
    setNotaAnalitoId(analitoId);
    setNotaMaterialLoteKey(materialLoteKey);
    setNotaActual(notaActual || '');
    setNotaModalVisible(true);
  }, []);

  // Guardar nota
  const handleSaveNota = useCallback(async () => {
    if (notaAnalitoId && notaMaterialLoteKey) {
      // Primero actualizar el estado local
      setAnalitos((prev) =>
        prev.map((a) => {
          if (a.Qcc_An_Id === notaAnalitoId) {
            return {
              ...a,
              controles: {
                ...a.controles,
                [notaMaterialLoteKey]: {
                  ...a.controles[notaMaterialLoteKey],
                  Nota: notaActual,
                },
              },
            };
          }
          return a;
        })
      );

      // Buscar el control y persistir la nota si ya existe una medición del día
      const analito = analitos.find((a) => a.Qcc_An_Id === notaAnalitoId);
      if (analito) {
        const control = analito.controles[notaMaterialLoteKey];
        if (control) {
          // Verificar si ya existe una medición del día actual
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);

          const esMedicionHoy = control.Qcme_Id && control.Qcme_FechaHora && new Date(ConvertLocalDate(control.Qcme_FechaHora)) >= today && new Date(ConvertLocalDate(control.Qcme_FechaHora)) < tomorrow;

          if (esMedicionHoy && control.Qcme_Id && control.Valor !== null && control.Valor !== undefined) {
            // Actualizar la medición existente con la nota
            try {
              const updateDto = {
                Qcme_Id: control.Qcme_Id,
                Qcme_Qcc_Id: control.Qcc_Id,
                Qcme_Valor: control.Valor,
                Qcme_Us_Id: userId,
                Qcme_ComentarioValidacion: notaActual || undefined,
              };

              const result = await updateMeasurement(updateDto);

              if (result) {
                // Invalidar caché de mediciones para esta configuración
                setMedicionesCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.delete(control.Qcc_Id);
                  return newCache;
                });
                newToast('SUCCESS', 'Nota guardada correctamente', 2);
              }
            } catch (error) {
              console.error('Error guardando nota:', error);
              newToast('ERROR', 'Error al guardar la nota', 5);
            }
          }
        }
      }

      setNotaModalVisible(false);
      setNotaActual('');
      setNotaAnalitoId(null);
      setNotaMaterialLoteKey('');

      // No es necesario hacer refresh, el estado ya se actualizó
    }
  }, [notaAnalitoId, notaMaterialLoteKey, notaActual, analitos, userId, updateMeasurement]);

  // Renderizar columna de nota
  const renderNotaColumn = useCallback(
    (materialLoteKey: string) => {
      return (cellData: any) => {
        const control = cellData.data.controles?.[materialLoteKey];
        if (!control) {
          return <div style={{ textAlign: 'center' }}>-</div>;
        }

        const tieneNota = control.Nota && control.Nota.trim() !== '';

        return (
          <div style={{ textAlign: 'center' }}>
            <Button icon={tieneNota ? resGridNotesCheck : resGridNotes} hint={tieneNota ? 'Ver/Editar nota' : 'Agregar nota'} onClick={() => handleOpenNotaModal(cellData.data.Qcc_An_Id, materialLoteKey, control.Nota || '')} stylingMode='text' type={tieneNota ? 'default' : 'normal'} />
          </div>
        );
      };
    },
    [handleOpenNotaModal]
  );

  // Generar columnas dinámicas según los controles seleccionados
  const renderDynamicColumns = useMemo(() => {
    if (!selectedMaterialLotes || selectedMaterialLotes.length === 0) {
      return null;
    }

    // Ordenar los materiales-lotes por nivel (NIVEL 1, NIVEL 2, NIVEL 3)
    const sortedMaterialLotes = [...selectedMaterialLotes].sort((a, b) => {
      const configKeyA = `${a}|${analitos[0]?.Qcc_An_Id || ''}`;
      const configKeyB = `${b}|${analitos[0]?.Qcc_An_Id || ''}`;
      const configA = configuracionesMap.get(configKeyA);
      const configB = configuracionesMap.get(configKeyB);
      const nivelA = configA?.MaterialNivel || '';
      const nivelB = configB?.MaterialNivel || '';

      // Extraer el número del nivel (NIVEL 1 -> 1, NIVEL 2 -> 2, etc.)
      const nivelNumA = nivelA.match(/\d+/)?.[0] || '999';
      const nivelNumB = nivelB.match(/\d+/)?.[0] || '999';

      return parseInt(nivelNumA) - parseInt(nivelNumB);
    });

    return sortedMaterialLotes.map((materialLoteKey) => {
      const [materialNombre, loteCodigo] = materialLoteKey.split('|');

      // Obtener el MaterialNivel desde la primera configuración disponible
      const configKey = `${materialLoteKey}|${analitos[0]?.Qcc_An_Id || ''}`;
      const primeraConfig = configuracionesMap.get(configKey);
      const materialNivel = primeraConfig?.MaterialNivel || '';

      // Crear el título de la columna agrupada: Material + Nivel
      const groupCaption = materialNivel ? `${materialNombre} - ${materialNivel}` : materialNombre;

      return (
        <Column key={`group_${materialLoteKey}`} caption={groupCaption} alignment='center'>
          <Column
            key={`valor_${materialLoteKey}`}
            caption='VALOR'
            dataField={`Valor_${materialLoteKey}`}
            width={120}
            alignment='center'
            dataType='number'
            format='#,##0.00'
            allowEditing={true}
            calculateCellValue={(rowData: any) => {
              return rowData.controles?.[materialLoteKey]?.Valor ?? null;
            }}
            setCellValue={(newData: any, value: any) => {
              if (!newData.controles) newData.controles = {};
              if (!newData.controles[materialLoteKey]) {
                newData.controles[materialLoteKey] = {};
              }
              newData[`Valor_${materialLoteKey}`] = value;
            }}
            cellRender={(cellData: any) => {
              const control = cellData.data.controles?.[materialLoteKey];
              if (!control) {
                return <div style={{ textAlign: 'center', color: '#ccc', fontStyle: 'italic' }}>N/A</div>;
              }
              const valor = control.Valor;
              if (valor === null || valor === undefined) {
                return <div style={{ textAlign: 'center' }}></div>;
              }
              return <div style={{ textAlign: 'center' }}>{valor.toFixed(2)}</div>;
            }}
          />
          <Column
            key={`media_${materialLoteKey}`}
            caption='MEDIA'
            width={120}
            alignment='center'
            dataType='number'
            format='#,##0.00'
            allowEditing={false}
            calculateCellValue={(rowData: any) => {
              return rowData.controles?.[materialLoteKey]?.Qcc_MediaEsperada ?? null;
            }}
            cellRender={(cellData: any) => {
              const control = cellData.data.controles?.[materialLoteKey];
              if (!control) {
                return <div style={{ textAlign: 'center', color: '#ccc', fontStyle: 'italic' }}>N/A</div>;
              }
              const media = control.Qcc_MediaEsperada;
              if (media === null || media === undefined) {
                return <div style={{ textAlign: 'center' }}>-</div>;
              }
              return <div style={{ textAlign: 'center' }}>{media.toFixed(2)}</div>;
            }}
          />
          <Column key={`nota_${materialLoteKey}`} caption='NOTA' width={80} alignment='center' allowEditing={false} cellRender={renderNotaColumn(materialLoteKey)} allowFiltering={false} allowSorting={false} />
        </Column>
      );
    });
  }, [selectedMaterialLotes, analitos, configuracionesMap, renderNotaColumn]);

  if (!Prm_QC_R) {
    return (
      <div className='view crm-contact-list'>
        <div className='view-wrapper'>
          <h3>No tiene permisos para acceder a este módulo</h3>
        </div>
      </div>
    );
  }

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-masive-measurements-qc'>
        <ScrollView width='100%' height='100%' showScrollbar='onScroll'>
          <div className='measurements-header'>
            {/* Título y Selector en la misma línea */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '15px' }}>
              <h2 style={{ margin: 0 }}>Ingreso Masivo de Mediciones</h2>
              <div className='selector-group'>
                <TagBox
                  dataSource={materialesLotes}
                  displayExpr={(item: any) => {
                    if (!item) return '';
                    return `${item.MaterialNombre} - Lote: ${item.LoteCodigo}`;
                  }}
                  valueExpr='MaterialLote'
                  value={selectedMaterialLotes}
                  onValueChanged={(e) => {
                    if (e.value) {
                      handleMaterialLoteChange(e.value);
                    } else {
                      handleMaterialLoteChange([]);
                    }
                  }}
                  placeholder='Seleccione uno o varios controles...'
                  searchEnabled={true}
                  showClearButton={true}
                  showSelectionControls={true}
                  applyValueMode='useButtons'
                  width={600}
                  showMultiTagOnly={false}
                  maxDisplayedTags={3}
                />
              </div>
            </div>

            {/* Información del control seleccionado */}
            {selectedMaterialLotes && selectedMaterialLotes.length > 0 && analitos.length > 0 && (
              <div className='control-selector'>
                <div className='control-info'>
                  <div className='info-badge'>
                    <strong>Controles Seleccionados:</strong> {selectedMaterialLotes.length}
                  </div>
                  <div className='info-badge'>
                    <strong>Total de Analitos:</strong> {analitos.length}
                  </div>
                  <div className='info-badge'>
                    <strong>Registrados:</strong>{' '}
                    {analitos.reduce((count, a) => {
                      return count + Object.values(a.controles || {}).filter((c: any) => c.Estado === 'saved').length;
                    }, 0)}
                  </div>
                  <div className='info-badge'>
                    <strong>Pendientes:</strong>{' '}
                    {analitos.reduce((count, a) => {
                      return count + Object.values(a.controles || {}).filter((c: any) => !c.Estado || c.Estado === 'pending').length;
                    }, 0)}
                  </div>
                  <div></div>
                  {/*  */}
                </div>
              </div>
            )}
          </div>

          {/* Tab Panel: Ingreso y Gráficos */}
          <TabPanel selectedIndex={selectedTab} onSelectedIndexChange={setSelectedTab} animationEnabled={true} swipeEnabled={false} height='calc(100vh - 350px)'>
            {/* Pestaña 1: Ingreso de Mediciones */}
            <TabItem title='MEDICIONES'>
              <div className='masive-grid-container'>
                <Toolbar>
                  <ToolbarItem location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                    <Button id='ActualizarMasivo' icon={resButtonRefresh} text='Actualizar' stylingMode='text' onClick={handleRefresh} />
                    <Tooltip target={'#ActualizarMasivo'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                      Refrescar lista de analitos
                    </Tooltip>
                  </ToolbarItem>
                </Toolbar>
                <DataGrid
                  className='grid'
                  dataSource={analitoDataSource}
                  ref={gridRef}
                  width={'100%'}
                  height={'100%'}
                  hoverStateEnabled={true}
                  repaintChangesOnly={true}
                  highlightChanges={true}
                  rowAlternationEnabled={true}
                  showColumnLines={true}
                  disabled={!selectedMaterialLotes || selectedMaterialLotes.length === 0 || !Prm_QC_C}
                  onEditorPreparing={(e) => {
                    if (e.dataField && e.dataField.startsWith('Valor_')) {
                      const materialLoteKey = e.dataField.replace('Valor_', '');
                      const control = e.row?.data?.controles?.[materialLoteKey];

                      if (!control) {
                        e.editorOptions.disabled = true;
                        return;
                      }

                      // Solo permitir editar valores del día actual
                      if (control.Qcme_FechaHora) {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const tomorrow = new Date(today);
                        tomorrow.setDate(tomorrow.getDate() + 1);

                        const fechaMedicion = new Date(ConvertLocalDate(control.Qcme_FechaHora));
                        const esMedicionHoy = fechaMedicion >= today && fechaMedicion < tomorrow;

                        if (!esMedicionHoy) {
                          e.editorOptions.disabled = true;
                          e.editorOptions.hint = 'Solo se pueden modificar mediciones del día actual';
                        }
                      }
                    }
                  }}>
                  <Editing mode='cell' allowUpdating={true} selectTextOnEditStart={true} startEditAction='click' />
                  <KeyboardNavigation editOnKeyPress={true} enterKeyAction='moveFocus' enterKeyDirection='column' />
                  <HeaderFilter visible={true} />
                  <Scrolling mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
                  <Sorting mode='multiple' />
                  <Selection mode='none' />
                  <StateStoring enabled={true} type='custom' />

                  {/* Columna fija de Analito */}
                  <Column caption='ANALITO' dataField='AnalitoNombre' alignment='left' allowEditing={false} fixed={true} sortOrder='asc' />

                  {/* Columnas dinámicas según controles seleccionados */}
                  {renderDynamicColumns}

                  {/* Columna de detalle */}
                  <Column caption='DETALLE' width={200} alignment='center' allowEditing={false} cellRender={renderVerDetalleButton} allowFiltering={false} allowSorting={false} />
                </DataGrid>
              </div>
            </TabItem>

            {/* Pestaña 2: Detalle del Analito */}
            <TabItem title='DETALLE DEL ANALITO'>
              {selectedConfigId ? (
                <div className='analito-detail-container'>
                  {/* Header con nombre del analito */}
                  <h4>{selectedAnalitoNombre}</h4>

                  {/* Grid de Mediciones */}
                  <div className='mediciones-grid-section'>
                    <DataGrid className='grid' dataSource={medicionesAnalito} ref={medicionesGridRef} width={'100%'} height={300} hoverStateEnabled={true} repaintChangesOnly={true} highlightChanges={true} rowAlternationEnabled={true} showColumnLines={true}>
                      <HeaderFilter visible={true} />
                      <Scrolling mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
                      <Sorting mode='multiple' />
                      <StateStoring enabled={false} />

                      <Column caption='NIVEL' dataField='NivelNombre' width={120} alignment='center' />
                      <Column
                        caption='FECHA/HORA'
                        dataField='Qcme_FechaHora'
                        width={180}
                        alignment='center'
                        dataType='datetime'
                        format='dd/MM/yyyy HH:mm'
                        defaultSortOrder='desc'
                        sortOrder='desc'
                        sortIndex={0}
                        calculateFilterExpression={(filterValue, selectedFilterOperation, target) => {
                          if (target === 'filterRow' || target === 'headerFilter') {
                            if (filterValue) {
                              const date = new Date(filterValue);
                              date.setHours(0, 0, 0, 0);
                              const nextDate = new Date(date);
                              nextDate.setDate(nextDate.getDate() + 1);

                              if (selectedFilterOperation === '=' || !selectedFilterOperation) {
                                return [['Qcme_FechaHora', '>=', date], 'and', ['Qcme_FechaHora', '<', nextDate]];
                              }

                              if (selectedFilterOperation === '>') {
                                return ['Qcme_FechaHora', '>=', nextDate];
                              }
                              if (selectedFilterOperation === '>=') {
                                return ['Qcme_FechaHora', '>=', date];
                              }
                              if (selectedFilterOperation === '<') {
                                return ['Qcme_FechaHora', '<', date];
                              }
                              if (selectedFilterOperation === '<=') {
                                return ['Qcme_FechaHora', '<', nextDate];
                              }
                            }
                          }
                          return [['Qcme_FechaHora', selectedFilterOperation || '=', filterValue]];
                        }}
                      />
                      <Column caption='VALOR' dataField='Qcme_Valor' width={100} alignment='center' dataType='number' format='#,##0.00' />
                      <Column caption='Z-SCORE' dataField='ZScore' width={120} alignment='center' dataType='number' format='#,##0.00' />
                      <Column
                        caption='ESTADO'
                        dataField='Qcme_Estado'
                        width={120}
                        alignment='center'
                        cellRender={renderEstadoCeldaMedicion}
                        calculateFilterExpression={(filterValue, selectedFilterOperation) => {
                          if (filterValue === 'PENDIENTE') {
                            return ['Qcme_Estado', '=', 1];
                          } else if (filterValue === 'VALIDADO') {
                            return ['Qcme_Estado', '=', 2];
                          } else if (filterValue === 'RECHAZADO') {
                            return ['Qcme_Estado', '=', 3];
                          } else if (filterValue === 'ALERTA') {
                            return ['Qcme_Estado', '=', 4];
                          }
                          return ['Qcme_Estado', selectedFilterOperation || '=', filterValue];
                        }}>
                        <HeaderFilter
                          dataSource={[
                            { text: 'PENDIENTE', value: 'PENDIENTE' },
                            { text: 'VALIDADO', value: 'VALIDADO' },
                            { text: 'RECHAZADO', value: 'RECHAZADO' },
                            { text: 'ALERTA', value: 'ALERTA' },
                          ]}
                        />
                      </Column>
                      <Column
                        caption='VIOLACIONES'
                        dataField='TieneViolaciones'
                        width={140}
                        alignment='center'
                        cellRender={renderViolacionCelda}
                        calculateFilterExpression={(filterValue, selectedFilterOperation) => {
                          if (filterValue === 'SI') {
                            return ['TieneViolaciones', '=', true];
                          } else if (filterValue === 'NO') {
                            return ['TieneViolaciones', '=', false];
                          }
                          return ['TieneViolaciones', selectedFilterOperation || '=', filterValue];
                        }}>
                        <HeaderFilter
                          dataSource={[
                            { text: 'SI', value: 'SI' },
                            { text: 'NO', value: 'NO' },
                          ]}
                        />
                      </Column>
                      <Column caption='TURNO' dataField='Qcme_Turno' width={110} alignment='center' />
                      <Column caption='COMENTARIO / VIOLACIONES' dataField='Qcme_Observaciones' minWidth={250} alignment='left' cellRender={renderComentarioCelda} />
                    </DataGrid>
                  </div>

                  {/* Gráficos Levey-Jennings - Uno por cada control */}
                  <div className='charts-section'>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h4 style={{ margin: 0 }}>Gráficos Levey-Jennings</h4>
                      <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
                        {chartsData && chartsData.length > 1 && <CheckBox text='Ver niveles juntos' value={nivelesJuntos} onValueChanged={(e) => setNivelesJuntos(e.value)} />}
                        <CheckBox text='Mostrar líneas' value={mostrarLineas} onValueChanged={(e) => setMostrarLineas(e.value)} />
                      </div>
                    </div>
                    {chartsData && chartsData.length > 0 ? (
                      nivelesJuntos ? (
                        // Vista combinada: todos los niveles en un solo gráfico
                        <div className='chart-section' style={{ marginBottom: '30px' }}>
                          {(() => {
                            // Preparar datos con una serie por nivel
                            const maxMediciones = Math.max(...chartsData.map((c) => c.data?.Mediciones?.length || 0));
                            const combinedData: any[] = [];

                            for (let i = 0; i < maxMediciones; i++) {
                              const dataPoint: any = { NumeroMedicion: i + 1 };

                              chartsData.forEach((chartInfo, chartIndex) => {
                                if (chartInfo.data && chartInfo.data.Mediciones && chartInfo.data.Mediciones[i]) {
                                  const m = chartInfo.data.Mediciones[i];
                                  dataPoint[`Valor_${chartIndex}`] = m.Valor;
                                  dataPoint[`Fecha_${chartIndex}`] = m.FechaHora;
                                  dataPoint[`Usuario_${chartIndex}`] = m.UsuarioNombre || 'N/A';
                                  dataPoint[`ColorPunto_${chartIndex}`] = m.ColorPunto || '#2196F3';
                                }
                              });

                              // Agregar líneas de referencia del primer nivel
                              if (chartsData[0] && chartsData[0].data) {
                                dataPoint.Media_Ref = chartsData[0].data.Media;
                                dataPoint.SD2Positivo_Ref = chartsData[0].data.Linea_2SD_Superior;
                                dataPoint.SD2Negativo_Ref = chartsData[0].data.Linea_2SD_Inferior;
                              }

                              combinedData.push(dataPoint);
                            }

                            const colors = ['#2196F3', '#FF9800', '#4CAF50', '#E91E63', '#9C27B0'];

                            return (
                              <Chart
                                dataSource={combinedData}
                                height={450}
                                customizePoint={(pointInfo: any) => {
                                  // Aplicar ColorPunto del backend solo a las series de mediciones
                                  if (pointInfo.seriesName && !pointInfo.seriesName.includes('Ref') && !pointInfo.seriesName.includes('SD') && !pointInfo.seriesName.includes('Media')) {
                                    const chartIndex = chartsData.findIndex((c) => c.controlName === pointInfo.seriesName);
                                    if (chartIndex >= 0 && pointInfo.point && pointInfo.point.data) {
                                      const colorPunto = pointInfo.point.data[`ColorPunto_${chartIndex}`];
                                      if (colorPunto) {
                                        return {
                                          color: colorPunto,
                                          hoverStyle: {
                                            color: colorPunto,
                                          },
                                        };
                                      }
                                    }
                                  }
                                  return {};
                                }}>
                                <ArgumentAxis title='Número de Medición' />
                                <ValueAxis title='Valor' />
                                <CommonSeriesSettings argumentField='NumeroMedicion' />

                                {/* Líneas de referencia - Declaradas primero para que queden debajo */}
                                {chartsData.length > 0 && chartsData[0].data && (
                                  <>
                                    <Series valueField='Media_Ref' name='Media (Ref)' color='#4CAF50' type='line' dashStyle='dash' width={2.5} opacity={0.6}>
                                      <Point visible={false} />
                                    </Series>
                                    <Series valueField='SD2Positivo_Ref' name='+2 SD (Ref)' color='#FF9800' type='line' dashStyle='dash' width={1.5} opacity={0.4}>
                                      <Point visible={false} />
                                    </Series>
                                    <Series valueField='SD2Negativo_Ref' name='-2 SD (Ref)' color='#FF9800' type='line' dashStyle='dash' width={1.5} opacity={0.4}>
                                      <Point visible={false} />
                                    </Series>
                                  </>
                                )}

                                {/* Serie de mediciones para cada nivel - Declaradas al final para que queden encima */}
                                {chartsData.map((chartInfo, chartIndex) => (
                                  <Series key={`mediciones-${chartIndex}`} valueField={`Valor_${chartIndex}`} name={chartInfo.controlName} color={colors[chartIndex % colors.length]} type={mostrarLineas ? 'line' : 'scatter'} width={mostrarLineas ? 3 : 0} hoverMode='onlyPoint'>
                                    <Point size={10} visible={true} hoverMode='onlyPoint' />
                                  </Series>
                                ))}

                                <Legend verticalAlignment='bottom' horizontalAlignment='center' />
                                <ChartTooltip
                                  enabled={true}
                                  shared={false}
                                  zIndex={10000}
                                  customizeTooltip={(pointInfo: any) => {
                                    if (pointInfo.seriesName && !pointInfo.seriesName.includes('Ref') && !pointInfo.seriesName.includes('SD')) {
                                      const chartIndex = chartsData.findIndex((c) => c.controlName === pointInfo.seriesName);
                                      if (chartIndex >= 0) {
                                        const fecha = pointInfo.point.data[`Fecha_${chartIndex}`];
                                        const usuario = pointInfo.point.data[`Usuario_${chartIndex}`];
                                        const nota = pointInfo.point.data[`Nota_${chartIndex}`];
                                        let fechaFormateada = 'N/A';

                                        if (fecha) {
                                          const fechaObj = new Date(ConvertLocalDate(fecha));
                                          fechaFormateada = fechaObj.toLocaleString('es-ES', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          });
                                        }

                                        return {
                                          html: `
                                            <div style="padding: 8px;">
                                              <div><strong>Nivel:</strong> ${pointInfo.seriesName}</div>
                                              <div><strong>Valor:</strong> ${pointInfo.valueText}</div>
                                              <div><strong>Fecha:</strong> ${fechaFormateada}</div>
                                              <div><strong>Usuario:</strong> ${usuario}</div>
                                              <div><strong>Nota:</strong> ${nota}</div>
                                            </div>
                                          `,
                                        };
                                      }
                                    }
                                    return { text: pointInfo.valueText };
                                  }}
                                />
                              </Chart>
                            );
                          })()}
                        </div>
                      ) : (
                        // Vista separada: un gráfico por nivel
                        chartsData.map((chartInfo, index) => (
                          <div key={chartInfo.controlKey} className='chart-section' style={{ marginBottom: '30px' }}>
                            <h5 style={{ marginBottom: '15px', color: '#333' }}>{chartInfo.controlName}</h5>
                            {chartInfo.data && chartInfo.data.Mediciones && chartInfo.data.Mediciones.length > 0 ? (
                              <Chart
                                dataSource={(() => {
                                  const dataPoints = chartInfo.data.Mediciones.map((m: any, idx: number) => {
                                    return {
                                      NumeroMedicion: idx + 1,
                                      Valor: m.Valor,
                                      Media: chartInfo.data.Media,
                                      SD1Positivo: chartInfo.data.Linea_1SD_Superior,
                                      SD1Negativo: chartInfo.data.Linea_1SD_Inferior,
                                      SD2Positivo: chartInfo.data.Linea_2SD_Superior,
                                      SD2Negativo: chartInfo.data.Linea_2SD_Inferior,
                                      SD3Positivo: chartInfo.data.Linea_3SD_Superior,
                                      SD3Negativo: chartInfo.data.Linea_3SD_Inferior,
                                      ColorPunto: m.ColorPunto || '#2196F3',
                                      Fecha: m.FechaHora,
                                      Usuario: m.UsuarioNombre || 'N/A',
                                    };
                                  });

                                  // Si solo hay un punto, agregar puntos invisibles al inicio y al final para centrarlo
                                  if (dataPoints.length === 1) {
                                    const lineaData = {
                                      Valor: null,
                                      Media: chartInfo.data.Media,
                                      SD1Positivo: chartInfo.data.Linea_1SD_Superior,
                                      SD1Negativo: chartInfo.data.Linea_1SD_Inferior,
                                      SD2Positivo: chartInfo.data.Linea_2SD_Superior,
                                      SD2Negativo: chartInfo.data.Linea_2SD_Inferior,
                                      SD3Positivo: chartInfo.data.Linea_3SD_Superior,
                                      SD3Negativo: chartInfo.data.Linea_3SD_Inferior,
                                      ColorPunto: null,
                                      Fecha: null,
                                      Usuario: null,
                                    };

                                    // Agregar 5 puntos invisibles al inicio
                                    for (let i = 5; i > 0; i--) {
                                      dataPoints.unshift({
                                        NumeroMedicion: -i,
                                        ...lineaData,
                                      });
                                    }

                                    // Ajustar el número de medición del punto real
                                    dataPoints[5].NumeroMedicion = 0;

                                    // Agregar 5 puntos invisibles al final
                                    for (let i = 1; i <= 5; i++) {
                                      dataPoints.push({
                                        NumeroMedicion: i,
                                        ...lineaData,
                                      });
                                    }
                                  }

                                  return dataPoints;
                                })()}
                                height={350}
                                customizePoint={(pointInfo: any) => {
                                  // Usar ColorPunto del backend que considera Estado, Violaciones y Severidad
                                  if (pointInfo.seriesName === 'Mediciones') {
                                    const colorPunto = pointInfo.data.ColorPunto;
                                    if (colorPunto) {
                                      return {
                                        color: colorPunto,
                                        hoverStyle: {
                                          color: colorPunto,
                                        },
                                      };
                                    }
                                  }
                                  return {};
                                }}>
                                <ArgumentAxis title='Número de Medición' />
                                <ValueAxis title='Valor' />
                                <CommonSeriesSettings argumentField='NumeroMedicion' />

                                {/* Líneas de control - Declaradas primero para que queden debajo */}
                                <Series valueField='Media' name='Media' color='#4CAF50' type='line' dashStyle='dash' width={2.5} opacity={0.6}>
                                  <Point visible={false} />
                                </Series>
                                <Series valueField='SD1Positivo' name='+1 SD' color='#FFC107' type='line' dashStyle='dash' width={1.5} opacity={0.4}>
                                  <Point visible={false} />
                                </Series>
                                <Series valueField='SD1Negativo' name='-1 SD' color='#FFC107' type='line' dashStyle='dash' width={1.5} opacity={0.4}>
                                  <Point visible={false} />
                                </Series>
                                <Series valueField='SD2Positivo' name='+2 SD' color='#FF9800' type='line' dashStyle='dash' width={1.5} opacity={0.4}>
                                  <Point visible={false} />
                                </Series>
                                <Series valueField='SD2Negativo' name='-2 SD' color='#FF9800' type='line' dashStyle='dash' width={1.5} opacity={0.4}>
                                  <Point visible={false} />
                                </Series>
                                <Series valueField='SD3Positivo' name='+3 SD' color='#F44336' type='line' dashStyle='dash' width={1.5} opacity={0.4}>
                                  <Point visible={false} />
                                </Series>
                                <Series valueField='SD3Negativo' name='-3 SD' color='#F44336' type='line' dashStyle='dash' width={1.5} opacity={0.4}>
                                  <Point visible={false} />
                                </Series>

                                {/* Serie principal de datos - Declarada al final para que quede encima */}
                                <Series valueField='Valor' name='Mediciones' color='#2196F3' type={mostrarLineas ? 'line' : 'scatter'} width={mostrarLineas ? 2 : 0} hoverMode='onlyPoint'>
                                  <Point size={8} visible={true} hoverMode='onlyPoint' />
                                </Series>

                                <Legend verticalAlignment='bottom' horizontalAlignment='center' />
                                <ChartTooltip
                                  enabled={true}
                                  shared={false}
                                  zIndex={10000}
                                  customizeTooltip={(pointInfo: any) => {
                                    if (pointInfo.seriesName === 'Mediciones') {
                                      const fecha = pointInfo.point.data.Fecha;
                                      const usuario = pointInfo.point.data.Usuario;
                                      let fechaFormateada = 'N/A';

                                      if (fecha) {
                                        const fechaObj = new Date(ConvertLocalDate(fecha));
                                        fechaFormateada = fechaObj.toLocaleString('es-ES', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        });
                                      }

                                      return {
                                        html: `
                                        <div style="padding: 8px;">
                                          <div><strong>Valor:</strong> ${pointInfo.valueText}</div>
                                          <div><strong>Fecha:</strong> ${fechaFormateada}</div>
                                          <div><strong>Usuario:</strong> ${usuario}</div>
                                        </div>
                                      `,
                                      };
                                    }
                                    return { text: pointInfo.valueText };
                                  }}
                                />
                              </Chart>
                            ) : (
                              <div className='no-chart-data'>
                                <p>No hay datos suficientes para generar el gráfico de {chartInfo.controlName}</p>
                              </div>
                            )}
                          </div>
                        ))
                      )
                    ) : (
                      <div className='no-chart-data'>
                        <p>No hay datos de gráficos disponibles</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className='no-analito-selected'>
                  <p>Seleccione un analito del grid de ingreso para ver su detalle</p>
                </div>
              )}
            </TabItem>
          </TabPanel>
        </ScrollView>

        {/* Modal para agregar/editar nota */}
        <Popup
          visible={notaModalVisible}
          onHiding={() => {
            setNotaModalVisible(false);
            setNotaActual('');
            setNotaAnalitoId(null);
            setNotaMaterialLoteKey('');
          }}
          dragEnabled={false}
          closeOnOutsideClick={true}
          showTitle={true}
          title='Nota de Medición'
          width={500}
          height={300}>
          <div style={{ padding: '10px' }}>
            <TextArea value={notaActual} onValueChanged={(e) => setNotaActual(e.value)} height={150} placeholder='Ingrese una nota para esta medición...' />
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button
                text='Cancelar'
                onClick={() => {
                  setNotaModalVisible(false);
                  setNotaActual('');
                  setNotaAnalitoId(null);
                  setNotaMaterialLoteKey('');
                }}
                stylingMode='outlined'
              />
              <Button text='Guardar' onClick={handleSaveNota} type='default' stylingMode='contained' />
            </div>
          </div>
        </Popup>
      </div>
    </div>
  );
};

export default MasiveMeasurementsQC;
